import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  primeiraFatura,
  textoDoPercentual,
  valorComDesconto,
  valorReajustado,
  zNovaAssinatura,
  type PlanoNaLista,
} from '@arena/shared/schemas';
import { dataValida } from '@arena/shared/tempo';
import { dinheiro, paraCentavos } from '../agenda/formatos.js';
import { useAtrasado } from '../agenda/useAtrasado.js';
import { errosPorCampo, primeiraMensagem } from '../alunos/formatos.js';
import { alunosApi } from '../api/alunos.js';
import { ErroDeApi, mensagemDoErro } from '../api/cliente.js';
import { planosApi } from '../api/planos.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { EsqueletoDeCard } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Formulario, Nota } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { competenciaPorExtenso, dinheiroComCentavos } from './formatos.js';

/**
 * Assinar um plano — "aplicar", na célula da Gestão.
 *
 * A pessoa da ficha é a TITULAR. No plano que cobre mais de uma pessoa, as
 * outras entram pela busca de alunos. O valor é o do plano, com o desconto
 * dele se marcado; um valor diferente só aparece para quem pode editar
 * planos — negociar preço é do Dono, e o servidor confere de novo.
 *
 * A prévia "Primeira fatura: 05/10 · R$ 160,00" é a mesma conta do
 * servidor (`primeiraFatura`, `valorComDesconto`), antes de confirmar.
 */
export function ModalDeAssinatura({
  alunoId,
  alunoNome,
  onFechar,
}: {
  alunoId: string;
  alunoNome: string;
  onFechar: () => void;
}) {
  const planos = useQuery({ queryKey: ['planos', 'lista'], queryFn: () => planosApi.listar() });

  if (planos.isPending) {
    return (
      <Modal aberto onFechar={onFechar} titulo="Assinar plano" largura="estreita">
        <CorpoDeModal>
          <EsqueletoDeCard linhas={4} />
        </CorpoDeModal>
      </Modal>
    );
  }
  if (planos.isError) {
    return (
      <Modal aberto onFechar={onFechar} titulo="Assinar plano" largura="estreita">
        <CorpoDeModal>
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{mensagemDoErro(planos.error)}</div>
          </div>
        </CorpoDeModal>
      </Modal>
    );
  }

  const ativos = planos.data.planos.filter((p) => p.status === 'Ativo');
  return (
    <FormularioDeAssinatura
      alunoId={alunoId}
      alunoNome={alunoNome}
      planos={ativos}
      hoje={planos.data.hoje}
      onFechar={onFechar}
    />
  );
}

function FormularioDeAssinatura({
  alunoId,
  alunoNome,
  planos,
  hoje,
  onFechar,
}: {
  alunoId: string;
  alunoNome: string;
  planos: readonly PlanoNaLista[];
  hoje: string;
  onFechar: () => void;
}) {
  const { pode } = useSessao();
  const avisar = useToast();
  const cliente = useQueryClient();
  const podeNegociar = pode('planos', 'editar');

  const [planoId, setPlanoId] = useState(planos[0]?.id ?? '');
  const plano = planos.find((p) => p.id === planoId);
  const [inicio, setInicio] = useState(hoje);
  const [dia, setDia] = useState<number | null>(null);
  const [aplicarDesconto, setAplicarDesconto] = useState(false);
  const [valor, setValor] = useState('');
  const [outros, setOutros] = useState<{ id: string; nome: string }[]>([]);
  const [busca, setBusca] = useState('');
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  const buscaAtrasada = useAtrasado(busca.trim(), 300);
  const cabeMais = plano ? outros.length < plano.pessoas - 1 : false;
  const candidatos = useQuery({
    queryKey: ['alunos', 'listar', buscaAtrasada, 'assinatura'],
    queryFn: () => alunosApi.listar({ busca: buscaAtrasada, limite: 6 }),
    enabled: cabeMais && buscaAtrasada.length >= 2,
  });

  const diaEfetivo = dia ?? plano?.diaVencimento ?? 5;
  const deTabela =
    plano && plano.reajusteProgramado && inicio >= plano.reajusteProgramado.vigencia
      ? plano.reajusteProgramado.valor
      : (plano?.valor ?? 0);
  const calculado =
    aplicarDesconto && plano?.desconto ? valorComDesconto(deTabela, plano.desconto) : deTabela;
  const negociado = valor.trim() ? paraCentavos(valor) : null;
  const primeira = dataValida(inicio) ? primeiraFatura(inicio, diaEfetivo) : null;
  // Como no servidor: o contrato que começa antes de um reajuste programado
  // ganha a linha do reajuste — e a primeira fatura que vence depois da
  // vigência já sai com o valor novo.
  const reajuste = plano?.reajusteProgramado;
  const valorDaPrimeira =
    primeira && reajuste && reajuste.vigencia > inicio && primeira.vencimento >= reajuste.vigencia
      ? valorReajustado(negociado ?? calculado, reajuste.percentual)
      : (negociado ?? calculado);

  const assinar = useMutation({
    mutationFn: planosApi.assinar,
    onSuccess: () =>
      Promise.all([
        cliente.invalidateQueries({ queryKey: ['cobrancas'] }),
        cliente.invalidateQueries({ queryKey: ['planos'] }),
      ]),
  });

  async function confirmar() {
    setErros({});
    setErroGeral(null);
    if (valor.trim() && negociado === null) {
      setErros({ valor: 'Valor inválido — use 160 ou 160,50.' });
      return;
    }
    const pedido = zNovaAssinatura.safeParse({
      planoId,
      alunoIds: [alunoId, ...outros.map((o) => o.id)],
      inicio,
      diaVencimento: diaEfetivo,
      aplicarDesconto,
      ...(negociado !== null ? { valor: negociado } : {}),
    });
    if (!pedido.success) {
      setErros(errosPorCampo(pedido.error.issues));
      return;
    }
    try {
      const criada = await assinar.mutateAsync(pedido.data);
      avisar(
        criada.faturas.length > 0
          ? `Assinatura criada. A fatura de ${competenciaPorExtenso(criada.faturas[criada.faturas.length - 1]!.competencia).toLowerCase()} já nasceu.`
          : `Assinatura criada. A primeira fatura nasce antes de ${criada.primeiraFatura.vencimento.slice(8, 10)}/${criada.primeiraFatura.vencimento.slice(5, 7)}.`,
      );
      onFechar();
    } catch (erro) {
      if (erro instanceof ErroDeApi && erro.campos) setErros(primeiraMensagem(erro.campos));
      setErroGeral(mensagemDoErro(erro));
    }
  }

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo="Assinar plano"
      descricao={`${alunoNome} como titular da assinatura.`}
      largura="estreita"
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void confirmar()} disabled={assinar.isPending || !plano}>
            {assinar.isPending ? 'Assinando…' : 'Assinar'}
          </Botao>
        </>
      }
    >
      <CorpoDeModal>
        {planos.length === 0 ? (
          <Nota>Nenhum plano ativo no catálogo.</Nota>
        ) : (
          <>
            <Campo rotulo="Plano" erro={erros['planoId']}>
              <select
                value={planoId}
                onChange={(e) => {
                  setPlanoId(e.target.value);
                  setDia(null);
                  setAplicarDesconto(false);
                  setOutros([]);
                }}
              >
                {planos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome} — {dinheiro(p.valor)}
                  </option>
                ))}
              </select>
            </Campo>
            <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
              <Campo rotulo="Início" erro={erros['inicio']}>
                <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />
              </Campo>
              <Campo rotulo="Dia de vencimento" erro={erros['diaVencimento']}>
                <select value={diaEfetivo} onChange={(e) => setDia(Number(e.target.value))}>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </Campo>
            </Formulario>

            {plano?.desconto && (
              <label className="consent">
                <input
                  className="ck"
                  type="checkbox"
                  checked={aplicarDesconto}
                  onChange={(e) => setAplicarDesconto(e.target.checked)}
                />
                <span>
                  Aplicar o desconto do plano
                  <small>
                    {plano.desconto.tipo === 'Percentual'
                      ? textoDoPercentual(plano.desconto.valor)
                      : dinheiro(plano.desconto.valor)}{' '}
                    — indicação, plano família
                  </small>
                </span>
              </label>
            )}

            {podeNegociar && (
              <Campo
                rotulo="Valor combinado"
                erro={erros['valor']}
                ajuda="Opcional. Vazio, vale o valor do plano."
              >
                <input
                  inputMode="decimal"
                  placeholder={dinheiroComCentavos(calculado)}
                  value={valor}
                  onChange={(e) => setValor(e.target.value)}
                />
              </Campo>
            )}

            {plano && plano.pessoas > 1 && (
              <Campo rotulo={`Outras pessoas (até ${plano.pessoas - 1})`} erro={erros['alunoIds']}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {outros.map((o) => (
                    <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{o.nome}</span>
                      <button
                        type="button"
                        className="link"
                        onClick={() => setOutros(outros.filter((x) => x.id !== o.id))}
                      >
                        remover
                      </button>
                    </div>
                  ))}
                  {cabeMais && (
                    <input
                      placeholder="Buscar aluno pelo nome"
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                    />
                  )}
                  {cabeMais &&
                    candidatos.data?.itens
                      .filter((a) => a.id !== alunoId && !outros.some((o) => o.id === a.id))
                      .map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          className="link"
                          style={{ textAlign: 'left' }}
                          onClick={() => {
                            setOutros([...outros, { id: a.id, nome: a.nome }]);
                            setBusca('');
                          }}
                        >
                          + {a.nome}
                        </button>
                      ))}
                </div>
              </Campo>
            )}

            {primeira && (
              <Nota>
                Primeira fatura: vencimento em{' '}
                <b>
                  {primeira.vencimento.slice(8, 10)}/{primeira.vencimento.slice(5, 7)}/
                  {primeira.vencimento.slice(0, 4)}
                </b>{' '}
                · <b>{dinheiroComCentavos(valorDaPrimeira)}</b>. Sem proporcional: quem entra depois
                do dia do vencimento paga a partir do mês seguinte.
              </Nota>
            )}
          </>
        )}

        {erroGeral && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{erroGeral}</div>
          </div>
        )}
      </CorpoDeModal>
    </Modal>
  );
}
